import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { UserModel } from '@fi-app/user/user.model';
import { DataFieldModel } from '@fi-app/data-field/data-field.model';
import { BehaviorSubject, Subject } from 'rxjs';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { IMediaBankFileReference } from '@fi-shared/media-bank';
import { FiImageSize, FileType, ObjectTypes } from '@fi-shared/types';
import { MediaBankStorageType } from '@fi-shared/media-bank/media-bank-storage-type';
import { MediaViewerService } from '@fi-shared/media-viewer/media-viewer.service';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { takeUntil } from 'rxjs/operators';
import { FiUserSharedService } from '@fi-shared/fi-user-shared/fi-user-shared.service';
import { IUserViewDialogData } from '@fi-app/user/user';

@Component({
    selector: 'fi-user-view-dialog',
    templateUrl: './fi-user-view-dialog.component.html',
    styleUrls: ['./fi-user-view-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-user-view-dialog' },
    standalone: false,
})
export class FiUserViewDialogComponent
    extends FiDialogsContentBase<IUserViewDialogData, { userModel: UserModel }>
    implements OnInit, OnDestroy
{
    loading$ = new BehaviorSubject<boolean>(false);

    private userSharedService: FiUserSharedService;
    private mediaViewerService: MediaViewerService;
    private fiMediaStorageService: FiMediaStorageService;
    private sanitizer: DomSanitizer;
    private fiStateService: FiStateService;

    FiStateNames = FiStateNames;
    protected get user(): UserModel {
        return this.data.user;
    }
    protected get userDataFields(): DataFieldModel[] {
        return this.data.userDataFields;
    }
    protected get hasEditAccess(): boolean {
        return this.data.hasEditAccess;
    }
    protected get businessUnitName(): string {
        return this.data.user.PreferredBusinessUnitName;
    }

    private isUserDeleteProcess: boolean = false;

    ngOnInit(): void {
        this.setServices();
        this.subscribeOnStateChange();
    }

    private subscribeOnStateChange() {
        this.fiStateService.onFiStateGo$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => this.closeDialog());
    }

    editUser(stateName: string) {
        this.fiStateService.go(stateName, { Id: this.user.Id });
    }

    deleteUser() {
        this.isUserDeleteProcess = true;
        this.closeDialog(this.user);
    }

    closeDialog(userModel?: UserModel) {
        this.dialogRef.close({ value: { userModel } });
    }

    getUserImage(): SafeStyle {
        if (this.user.MediaBankPhysicalFileName) {
            const url = this.fiMediaStorageService.getMediaFileUrl(
                {
                    PhysicalFileName: this.user.MediaBankPhysicalFileName,
                    Extension: this.user.MediaBankExtension,
                },
                {
                    size: FiImageSize.Medium,
                },
            );
            return this.sanitizer.bypassSecurityTrustStyle(`url(${url})`);
        }
    }

    openEnlargedView() {
        if (this.user.isLocked() || !this.user.MediaBankPhysicalFileName?.length) {
            return;
        }

        const mediaBankFileReference: IMediaBankFileReference = {
            ObjectType: ObjectTypes.MediaBank,
            PhysicalFileName: this.user.MediaBankPhysicalFileName,
            Extension: this.user.MediaBankExtension,
            Name: this.user.MediaBankPhysicalFileName,
            FileType: FileType.Image,
            StorageType: MediaBankStorageType.Azure,
        };
        const mediaBankFileUrl: string = this.fiMediaStorageService.getMediaFileUrl(
            {
                PhysicalFileName: this.user.MediaBankPhysicalFileName,
                Extension: this.user.MediaBankExtension,
            },
            { size: FiImageSize.Medium },
        );

        this.mediaViewerService.Show(mediaBankFileUrl, undefined, [mediaBankFileReference]);
    }

    getLatestActivity(): string {
        return this.userSharedService.getLatestActivity(this.user.LatestActivity, this.hasEditAccess);
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    private setServices() {
        this.userSharedService = this.injector.get(FiUserSharedService);
        this.mediaViewerService = this.injector.get(MediaViewerService);
        this.fiMediaStorageService = this.injector.get(FiMediaStorageService);
        this.sanitizer = this.injector.get(DomSanitizer);
        this.fiStateService = this.injector.get(FiStateService);
    }
}
